<template>
  <div class="home-page">
    <div class="container-fluid px-4 py-4">
      <!-- Main Layout -->
      <div class="main-layout">
        <!-- Events Section -->
        <section class="events-section">
          <!-- Events Count -->
          <div class="events-count" v-html="$tc('home.eventsFound', filteredWorkshops.length, { count: filteredWorkshops.length })"></div>

          <!-- Loading State -->
          <div class="events-grid" v-if="loading">
            <div
              v-for="n in 6"
              :key="n"
              class="event-card loading-skeleton"
            >
              <div class="skeleton-image"></div>
              <div class="event-content">
                <div class="skeleton-text medium"></div>
                <div class="skeleton-text short"></div>
                <div class="skeleton-text"></div>
                <div class="skeleton-text medium"></div>
              </div>
            </div>
          </div>

          <!-- Events Grid/List -->
          <div
            :class="displayMode === 'grid' ? 'events-grid' : 'events-list'"
            v-else-if="filteredWorkshops.length > 0"
          >
            <EventCard
              v-for="workshop in filteredWorkshops"
              :key="workshop.uuid"
              :workshop="workshop"
              :display-mode="displayMode"
              @reserve="openReservationModal"
            />
          </div>

          <!-- No Events -->
          <div v-else class="text-center py-5">
            <p class="text-muted">
              {{ selectedDate || hasActiveFilters ? $t('home.noEventsWithFilters') : $t('home.noEvents') }}
            </p>
          </div>
        </section>

        <!-- Sidebar -->
        <section class="sidebar-section" v-if="!privateAccessMode">
          <Sidebar
            :event-dates="eventDates"
            :available-themes="availableThemes"
            @date-selected="onDateSelected"
            @filter-changed="onFilterChanged"
            @search-changed="onSearchChanged"
            @display-mode-changed="onDisplayModeChanged"
          />
        </section>
      </div>
    </div>

    <!-- Mobile Bottom Bar -->
    <div class="mobile-filter-bar" v-if="!privateAccessMode">
      <button v-if="!$store.state.placeCode" class="filter-bar-btn" @click="openStoresModal">
        <b-icon icon="geo-alt"></b-icon>
        {{ $t('sidebar.stores') }}
      </button>
      <button class="filter-bar-btn" @click="openFiltersModal">
        <b-icon icon="sliders"></b-icon>
        {{ $t('sidebar.filters') }}
      </button>
    </div>

    <!-- Mobile Stores Modal -->
    <b-modal
      v-model="showStoresModal"
      :title="$t('sidebar.stores')"
      hide-footer
      centered
      modal-class="mobile-filter-modal"
      @hidden="onMobileFiltersClosed"
    >
      <StoreFilter @filter-changed="onMobileFilterChanged" />
    </b-modal>

    <!-- Mobile Filters Modal -->
    <b-modal
      v-model="showFiltersModal"
      :title="$t('sidebar.filters')"
      hide-footer
      centered
      scrollable
      modal-class="mobile-filter-modal"
      @hidden="onMobileFiltersClosed"
    >
      <!-- Mobile Search -->
      <div class="mobile-search-wrapper mb-3">
        <b-form-input
          :placeholder="$t('sidebar.searchPlaceholder')"
          :value="$store.state.searchQuery"
          @input="onMobileSearchInput"
        />
      </div>

      <!-- Mobile Calendar -->
      <Calendar
        :event-dates="eventDates"
        @date-selected="onMobileDateSelected"
      />

      <!-- Mobile Theme Filter -->
      <ThemeFilter
        :available-themes="availableThemes"
        @filter-changed="onMobileFilterChanged"
      />

      <!-- Mobile Price Filter -->
      <PriceFilter @filter-changed="onMobileFilterChanged" />
    </b-modal>

    <!-- Reservation Modal -->
    <b-modal
      id="reservationModal"
      :title="$t('modals.participant.title')"
      hide-footer
      centered
    >
      <b-form @submit.prevent="submitReservation" v-if="currentWorkshop">
        <b-form-group
          :label="$t('modals.participant.form.email')"
          label-for="email"
          class="required"
        >
          <b-form-input
            id="email"
            type="email"
            v-model="participantForm.email"
            required
            :disabled="submitting"
          ></b-form-input>
        </b-form-group>

        <b-form-group
          :label="$t('modals.participant.form.firstname')"
          label-for="firstname"
          v-if="isFieldAsked('firstname')"
          :class="{ 'required': isFieldMandatory('firstname') }"
        >
          <b-form-input
            id="firstname"
            type="text"
            v-model="participantForm.firstname"
            :required="isFieldMandatory('firstname')"
            :disabled="submitting"
          ></b-form-input>
        </b-form-group>

        <b-form-group
          :label="$t('modals.participant.form.lastname')"
          label-for="lastname"
          v-if="isFieldAsked('lastname')"
          :class="{ 'required': isFieldMandatory('lastname') }"
        >
          <b-form-input
            id="lastname"
            type="text"
            v-model="participantForm.lastname"
            :required="isFieldMandatory('lastname')"
            :disabled="submitting"
          ></b-form-input>
        </b-form-group>

        <b-form-group
          :label="$t('modals.participant.form.phone')"
          label-for="phone"
          v-if="isFieldAsked('phone')"
          :class="{ 'required': isFieldMandatory('phone') }"
        >
          <b-form-input
            id="phone"
            type="tel"
            v-model="participantForm.phone"
            :required="isFieldMandatory('phone')"
            :disabled="submitting"
          ></b-form-input>
        </b-form-group>

        <!-- Group Booking Section -->
        <hr class="my-3" v-if="currentWorkshop && currentWorkshop.availableSeats > 1" />

        <b-form-checkbox
          v-if="currentWorkshop && currentWorkshop.availableSeats > 1"
          v-model="participantForm.multiplePersons"
          :disabled="submitting"
          class="mb-3"
        >
          {{ $t('groupBooking.multiplePersons') }}
        </b-form-checkbox>

        <div v-if="participantForm.multiplePersons && currentWorkshop">
          <div
            v-for="(guest, index) in participantForm.guests"
            :key="index"
            class="d-flex align-items-center mb-2"
          >
            <b-form-input
              :placeholder="$t('groupBooking.guestFirstname', { number: index + 1 })"
              v-model="participantForm.guests[index]"
              :disabled="submitting"
              class="flex-grow-1"
            ></b-form-input>
            <b-button
              variant="outline-danger"
              size="sm"
              class="ml-2"
              @click="removeGuest(index)"
              :disabled="submitting"
            >
              <b-icon icon="x"></b-icon>
            </b-button>
          </div>

          <b-button
            variant="outline-secondary"
            size="sm"
            @click="addGuest"
            :disabled="submitting || !canAddMoreGuests"
            class="mb-3"
          >
            <b-icon icon="plus"></b-icon>
            {{ $t('groupBooking.addGuest') }}
          </b-button>

          <b-alert
            v-if="!hasEnoughSeats"
            show
            variant="warning"
            class="mb-3"
          >
            {{ $t('groupBooking.notEnoughSeats') }}
          </b-alert>
        </div>

        <div class="text-center mt-4">
          <b-button
            type="submit"
            variant="primary"
            :disabled="submitting"
            class="px-5"
          >
            <b-spinner small v-if="submitting"></b-spinner>
            <span v-else>{{ $t('modals.participant.submit') }}</span>
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <SeriesPickerModal
      :visible.sync="seriesPickerVisible"
      :workshop="seriesPickerWorkshop"
      :private-access-token="privateToken"
      @session-selected="onSeriesSessionSelected"
    />
  </div>
</template>

<script>
import ApiService from './services/api'
import IframeService from './services/iframe'
import EventCard from './components/Events/EventCard.vue'
import SeriesPickerModal from './components/Events/SeriesPickerModal.vue'
import Sidebar from './components/Sidebar/Sidebar.vue'
import StoreFilter from './components/Sidebar/StoreFilter.vue'
import Calendar from './components/Sidebar/Calendar.vue'
import ThemeFilter from './components/Sidebar/ThemeFilter.vue'
import PriceFilter from './components/Sidebar/PriceFilter.vue'

// Réponse 403 de login-register pour un compte pro/Extranet
// (WorkshopApiController::workshopsLoginRegisterAction)
const SELF_SERVICE_ACCOUNT_REFUSED = 'This account cannot be used through the self-service funnel.'

// Simple debounce implementation
function debounce(fn, delay) {
  let timeoutId
  return function (...args) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn.apply(this, args), delay)
  }
}

export default {
  name: 'Home',

  components: {
    EventCard,
    SeriesPickerModal,
    Sidebar,
    StoreFilter,
    Calendar,
    ThemeFilter,
    PriceFilter
  },

  data() {
    return {
      loading: false,
      workshops: [],
      displayMode: 'grid',
      currentWorkshop: null,
      submitting: false,
      showStoresModal: false,
      showFiltersModal: false,
      participantForm: {
        email: '',
        firstname: '',
        lastname: '',
        phone: '',
        multiplePersons: false,
        guests: []
      },
      privateAccessMode: false,
      privateToken: null,
      privateWorkshopUuid: null,
      seriesPickerVisible: false,
      seriesPickerWorkshop: null
    }
  },

  computed: {
    selectedDate() {
      return this.$store.state.selectedDate
    },

    selectedPlaces() {
      return this.$store.state.selectedPlaces
    },

    hasActiveFilters() {
      const allPlaces = this.$store.state.places
      const hasPlaceFilter = this.selectedPlaces.length !== allPlaces.length
      const hasThemeFilter = this.$store.state.selectedThemes.length > 0
      const hasPriceFilter = this.$store.state.priceFilter !== null
      const hasSearchQuery = this.$store.state.searchQuery.trim() !== ''
      return hasPlaceFilter || hasThemeFilter || hasPriceFilter || hasSearchQuery
    },

    availableThemes() {
      const themes = new Map()
      this.workshops.forEach(w => {
        if (w.workshopTheme) {
          themes.set(w.workshopTheme.uuid, w.workshopTheme)
        }
      })
      return Array.from(themes.values()).sort((a, b) => a.name.localeCompare(b.name))
    },

    filteredWorkshops() {
      let workshops = this.workshops

      // In private access mode, don't apply any filters
      if (this.privateAccessMode) {
        return workshops
      }

      // Filter by selected places
      if (this.selectedPlaces.length === 0) {
        // No place selected = no events
        return []
      } else if (this.selectedPlaces.length !== this.$store.state.places.length) {
        // Some places selected = filter
        const selectedCodes = this.selectedPlaces.map(p => String(p.code))
        workshops = workshops.filter(w => w.place && selectedCodes.includes(String(w.place.code)))
      }
      // All places selected = no filter needed

      // Filter by selected date
      if (this.selectedDate) {
        workshops = workshops.filter(w => {
          const workshopDate = this.$dayjs(w.datetime).format('YYYY-MM-DD')
          return workshopDate === this.selectedDate
        })
      }

      // Filter by selected themes
      const selectedThemes = this.$store.state.selectedThemes
      if (selectedThemes.length > 0) {
        workshops = workshops.filter(w =>
          w.workshopTheme && selectedThemes.includes(w.workshopTheme.uuid)
        )
      }

      // Filter by price
      const priceFilter = this.$store.state.priceFilter
      if (priceFilter === 'free') {
        workshops = workshops.filter(w => !w.price || w.price === 0)
      } else if (priceFilter === 'paid') {
        workshops = workshops.filter(w => w.price && w.price > 0)
      }

      return workshops
    },

    eventDates() {
      return [...new Set(
        this.workshops.map(w => this.$dayjs(w.datetime).format('YYYY-MM-DD'))
      )]
    },

    totalSeatsNeeded() {
      return 1 + this.participantForm.guests.filter(g => g.trim() !== '').length
    },

    canAddMoreGuests() {
      if (!this.currentWorkshop) return false
      return this.totalSeatsNeeded < this.currentWorkshop.availableSeats
    },

    hasEnoughSeats() {
      if (!this.currentWorkshop) return true
      return this.totalSeatsNeeded <= this.currentWorkshop.availableSeats
    }
  },

  created() {
    // Initialize debounced fetch function
    this.debouncedFetchWorkshops = debounce(() => {
      this.fetchWorkshops()
      IframeService.scrollParentToTop()
    }, 300)
  },

  mounted() {
    // Check for single-workshop access parameters in URL.
    // uuid seul = event public affiché seul ; token requis uniquement pour un event privé
    // (l'API ne vérifie le token que si le workshop est privé).
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token')
    const uuid = urlParams.get('uuid')

    if (uuid) {
      this.privateAccessMode = true
      this.privateToken = token || null
      this.privateWorkshopUuid = uuid
      this.fetchPrivateWorkshop()
    } else {
      this.fetchWorkshops()
    }
  },

  methods: {
    async fetchWorkshops() {
      this.loading = true

      try {
        const searchQuery = this.$store.state.searchQuery.trim()
        const response = await ApiService.getWorkshops({
          placeCode: this.$store.state.placeCode,
          search: searchQuery || undefined
        })

        this.workshops = response.data
          .filter(workshop => workshop.status === 'published')
          .map(workshop => {
            if (typeof workshop.askedFields === 'object') {
              workshop.askedFields = Object.values(workshop.askedFields)
            }
            if (typeof workshop.mandatoryFields === 'object') {
              workshop.mandatoryFields = Object.values(workshop.mandatoryFields)
            }
            return workshop
          })
      } catch (error) {
        console.error('Error fetching workshops:', error)
        this.$swal(this.$t('errors.base'))
      } finally {
        this.loading = false
      }
    },

    async fetchPrivateWorkshop() {
      this.loading = true

      try {
        const response = await ApiService.getPrivateWorkshop(this.privateWorkshopUuid, this.privateToken)

        let workshop = response.data
        if (typeof workshop.askedFields === 'object') {
          workshop.askedFields = Object.values(workshop.askedFields)
        }
        if (typeof workshop.mandatoryFields === 'object') {
          workshop.mandatoryFields = Object.values(workshop.mandatoryFields)
        }

        this.workshops = [workshop]
      } catch (error) {
        console.error('Error fetching private workshop:', error)
        if (error.response?.status === 403 || error.response?.status === 404) {
          this.$swal(this.$t('errors.privateAccessDenied'))
        } else {
          this.$swal(this.$t('errors.base'))
        }
        this.workshops = []
      } finally {
        this.loading = false
      }
    },

    onDateSelected() {
      // Date filtering is done in computed property
      IframeService.scrollParentToTop()
    },

    openStoresModal() {
      // En iframe, les modales s'ouvrent alignées en haut de l'iframe (mp-embedded) :
      // il faut remonter la page hôte, sinon le clic semble sans effet
      IframeService.scrollParentToTop()
      this.showStoresModal = true
    },

    openFiltersModal() {
      IframeService.scrollParentToTop()
      this.showFiltersModal = true
    },

    onMobileDateSelected(date) {
      this.$store.dispatch('setSelectedDate', date)
      this.showFiltersModal = false
    },

    onMobileFilterChanged() {
      // Filters are reactive, modal stays open for multiple selections
    },

    onMobileFiltersClosed() {
      IframeService.scrollParentToTop()
    },

    onMobileSearchInput(value) {
      this.$store.dispatch('setSearchQuery', value)
      this.debouncedFetchWorkshops()
    },

    onSearchChanged() {
      this.debouncedFetchWorkshops()
    },

    onFilterChanged() {
      // Client-side filters (theme, price) are reactive via computed property
      // No need to re-fetch from API
      IframeService.scrollParentToTop()
    },

    onDisplayModeChanged(mode) {
      this.displayMode = mode
    },

    openReservationModal(workshop) {
      IframeService.scrollParentToTop()

      if (workshop && workshop.kind === 'series') {
        this.seriesPickerWorkshop = workshop
        this.seriesPickerVisible = true
        return
      }

      this.currentWorkshop = workshop
      this.resetParticipantForm()
      this.$bvModal.show('reservationModal')
    },

    onSeriesSessionSelected(session) {
      // The chosen session is a regular Workshop child — reuse the existing reservation flow.
      this.currentWorkshop = session
      this.resetParticipantForm()
      this.$bvModal.show('reservationModal')
    },

    resetParticipantForm() {
      this.participantForm = {
        email: '',
        firstname: '',
        lastname: '',
        phone: '',
        multiplePersons: false,
        guests: []
      }
    },

    addGuest() {
      if (this.canAddMoreGuests) {
        this.participantForm.guests.push('')
      }
    },

    removeGuest(index) {
      this.participantForm.guests.splice(index, 1)
    },

    isFieldAsked(field) {
      return this.currentWorkshop?.askedFields?.includes(field)
    },

    isFieldMandatory(field) {
      return this.currentWorkshop?.mandatoryFields?.includes(field)
    },

    async submitReservation() {
      this.submitting = true

      try {
        // Login or register
        const loginResponse = await ApiService.loginOrRegister({
          email: this.participantForm.email,
          firstname: this.participantForm.firstname,
          lastname: this.participantForm.lastname,
          phone: this.participantForm.phone,
          locale: this.$i18n.locale
        })

        this.$store.dispatch('setUserToken', loginResponse.data.jwt)

        // Get session
        const sessionResponse = await ApiService.getSession()
        this.$store.dispatch('setUser', sessionResponse.data)

        // Register to workshop with guests
        const guests = this.participantForm.multiplePersons
          ? this.participantForm.guests.filter(g => g.trim() !== '')
          : []

        // Build payment return URLs.
        // Le back (InitiateWorkshopPaymentCommandHandler::resolvePaymentUrl) ignore
        // ces URLs si elles ne contiennent pas c=<clé de contexte>.
        const returnParams = new URLSearchParams()
        returnParams.set('c', this.$store.state.placeContext.key)
        if (this.$route.query.f) returnParams.set('f', this.$route.query.f)
        if (this.$route.query.l) returnParams.set('l', this.$route.query.l)
        if (this.$route.query.logo) returnParams.set('logo', this.$route.query.logo)
        if (this.$route.query.logoH) returnParams.set('logoH', this.$route.query.logoH)
        if (this.$route.query.pc) returnParams.set('pc', this.$route.query.pc)
        returnParams.set('w', this.currentWorkshop.name)

        const baseUrl = window.location.origin
        const successUrl = `${baseUrl}/payment/success?${returnParams.toString()}`
        const cancelUrl = `${baseUrl}/payment/cancel?${returnParams.toString()}`

        const response = await ApiService.registerToWorkshop(
          this.currentWorkshop.uuid,
          guests,
          successUrl,
          cancelUrl
        )

        // Handle payment required response
        if (response.data.status === 'payment_required') {
          // Store pending registration info for the return page
          sessionStorage.setItem('pendingWorkshopRegistration', JSON.stringify({
            workshopUuid: this.currentWorkshop.uuid,
            workshopName: this.currentWorkshop.name,
            participationUuid: response.data.participation_uuid,
            orderReference: response.data.order_reference
          }))

          // Redirect to Stripe Checkout — hors de l'iframe (Stripe refuse d'être framé)
          IframeService.redirectTopLevel(response.data.payment.checkout_url)
          return // Important: stop execution here
        }

        // Free workshop: success immediate
        this.$bvModal.hide('reservationModal')
        IframeService.scrollParentToTop()

        this.$swal(
          this.$t('workshops.notifications.registered.title'),
          this.$t('workshops.notifications.registered.message'),
          'success'
        )

        // Refresh workshops (en mode event unique, recharger uniquement cet event)
        if (this.privateAccessMode) {
          this.fetchPrivateWorkshop()
        } else {
          this.fetchWorkshops()
        }
      } catch (error) {
        console.error('Error during reservation:', error)
        IframeService.scrollParentToTop()

        if (error.response?.status === 400 && error.response?.data?.message) {
          this.$swal(this.$t('workshops.errors.' + error.response.data.message))
        } else if (error.response?.status === 403 && error.response?.data?.message === SELF_SERVICE_ACCOUNT_REFUSED) {
          this.$swal(this.$t('workshops.errors.email_not_allowed'))
        } else {
          this.$swal(this.$t('errors.base'))
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.required label::after {
  content: ' *';
  color: red;
}
</style>
